import { api } from "@/api"
import type { TableOrdering } from "@/components/BaseTable.vue"
import type {
  PaginatedResponse,
  PaginationQuery,
} from "@/utils/utilityTypes"
import { useQuery } from "@tanstack/vue-query"
import type { MaybeRef } from "@vueuse/core"

type ListMerchantGroupUsersQuery = {
  search?: string
  ordering?: TableOrdering<ListMerchantGroupUsersOrdering>
  schemaName: string
} & PaginationQuery

export type ListMerchantGroupUsersFilter = Pick<
  ListMerchantGroupUsersQuery,
  "search"
>

export type ListMerchantGroupUsersOrdering =
  | "email"
  | "name"
  | "lastLogin"
  | "createdAt"

export type ListMerchantGroupUsersResponseItem = {
  id: string
  email: string
  firstName: string
  lastName: string
  lastLogin: Date | null
  createdAt: Date
  merchantName: string
  isActive: boolean
}

const listMerchantGroupUsers = async (
  query: ListMerchantGroupUsersQuery
): Promise<
  PaginatedResponse<ListMerchantGroupUsersResponseItem>
> => {
  const { body } = await api.get<
    PaginatedResponse<
      RecursivelyConvertDatesToStrings<ListMerchantGroupUsersResponseItem>
    >
  >("/merchant-group/users/", {
    query: query,
    isMerchantGroup: true,
  })

  return {
    ...body,
    items: body.items.map((item) => ({
      ...item,
      lastLogin: item.lastLogin
        ? DateFns.parseISO(item.lastLogin)
        : null,
      createdAt: DateFns.parseISO(item.createdAt),
    })),
  }
}

type UseMerchantGroupUsersQueryKey = Omit<
  ListMerchantGroupUsersQuery,
  "schemaName"
> & {
  schemaName: string | undefined
}

export const useMerchantGroupUsers = (
  query: MaybeRef<UseMerchantGroupUsersQueryKey>
) =>
  useQuery({
    queryKey: [
      BASE_MERCHANT_GROUP_USERS_QUERY_KEY,
      query,
    ] as const,
    queryFn: ({ queryKey: [_, query_] }) =>
      listMerchantGroupUsers(
        unref(query_) as ListMerchantGroupUsersQuery
      ),
    keepPreviousData: true,
  })
